// engines/entitlement-service.js — the free-vs-paid gate (DOM-free).
//
// Phase-1 gating per Brief 005a. This engine answers ONE question for the
// screens: "is item X unlocked for the current user?". It reads from a
// swappable **source** (`entitlement-source.js`) — a stub in dev (005a), the
// real `/entitlements` fetch later (005b) — without the gate logic changing
// across that swap.
//
// Architecture rule #1: this is a pure state machine. It owns no DOM. It
// returns booleans/strings; `<rm-player>` and `<rm-upgrade-card>` do the
// rendering.
//
// Phase-1 is **UI-only** (signed media URLs are deferred — addendum §6). This
// validates the paid/free *experience*, not media security. Do NOT treat the
// 005a stub as a production paywall.
//
// Soft-fail contract: on ANY error from the source, the gate falls back to the
// FREE_ITEMS allowlist. The user is never hard-locked out by a flaky source.

import { fetchEntitlement } from "./entitlement-source.js";
import * as content from "./content-loader.js";

/**
 * The free allowlist: item IDs playable without entitlement. The free-set
 * **count is just the length of this list** — trivial to change later.
 *
 * Model decision (Brief 005a): anonymous + fixed allowlist (not free-account,
 * not rolling-N). Brief 018 tightens this to Volume-1 intro + the taxi lesson
 * (lesson 01) across all four modalities, so whole volumes (RM2, RM3, the
 * Third Ear Highlights) lock and only the Vol-1 "taste" is free.
 *
 * HTML-only items ("How To", methodology, About) are **never gated** at the
 * screen layer (free browse), so they don't strictly need to be listed here;
 * the recommended sub-folder intros are included anyway so an opened sub-folder
 * shows its own intro rather than a greyed one (Brief 018).
 *
 * `RM01_AUD_MH01` is an AudioHTML item; its companion audio (`RM01MH01`) is
 * unlocked automatically at init() so the memory-hook audio plays (see
 * `_precompute`).
 *
 * @type {readonly string[]}
 */
export const FREE_ITEMS = [
  "RM01INTRO",     // "Welcome! A few words from Chris Lonsdale" (Audio)
  "RM0101",        // taxi — Start by Just Listening (RM01NL)
  "RM0101PY",      // taxi — Listen & Watch (Pin Yin)
  "RM0101SC",      // taxi — Listen & Watch (Characters)
  "RM01_AUD_MH01", // taxi — Bonus Program / Memory Hook (AudioHTML; companion RM01MH01)
  "RMNLINTRO",     // "Why start with no lyrics?" sub-folder intro (HTML — never gated anyway)
  "RM01MHINTRO",   // "Volume 1 Bonus Program Introduction" (Audio)
  "RM0109",        // "Turn Left" demo song — Audio-only track ONLY (the directions song,
                   //  title "Go straight, then turn right."). Its PY/SC/memory-hook variants
                   //  (RM0109PY, RM0109SC, RM01MH09) stay LOCKED. Pairs with the sales-page
                   //  sample → "hear the full song in the demo" click-through.
];

/** @typedef {"entitled"|"anonymous"|"expired"} EntitlementStatus */

/**
 * Item IDs unlocked without entitlement: the FREE_ITEMS allowlist plus any
 * companion audio of a free AudioHTML item (filled in by `_precompute`).
 * @type {Set<string>}
 */
const _unlockedItems = new Set(FREE_ITEMS);

/**
 * List IDs that are unlocked without entitlement: every list with ≥1 free
 * descendant item (transitive). Precomputed once at init() by walking the
 * content tree, then read O(1) by `isListUnlocked`.
 * @type {Set<string>}
 */
const _unlockedLists = new Set();

/** True once the content-tree walk has run (memoised — static across states). */
let _precomputed = false;

/** Whether the current user is fully entitled (all items unlocked). */
let _entitled = false;
/** @type {EntitlementStatus} */
let _status = "anonymous";
/** True once init() has resolved (success OR soft-fail). */
let _initialized = false;

/**
 * Resolve the current entitlement from the source and compute the unlocked
 * set. Idempotent-ish: calling again re-fetches. Soft-fails to the free set on
 * any error — never throws, never hard-locks.
 *
 * @returns {Promise<void>}
 */
export async function init() {
  try {
    const res = await fetchEntitlement(); // { access: boolean, status?: EntitlementStatus }
    _entitled = !!res?.access;
    _status = /** @type {EntitlementStatus} */ (
      res?.status ?? (_entitled ? "entitled" : "anonymous")
    );
  } catch (err) {
    // Soft-fail: a flaky/absent source must not lock the user out. Fall back
    // to the free set (Brief 005a "Confirm soft-fail" verification step).
    console.warn("[entitlement] source failed; falling back to free set", err);
    _entitled = false;
    _status = "anonymous";
  }
  // Precompute the unlocked-list set (Brief 018). Independent of entitlement
  // state — the free set is static — so it runs once and is memoised.
  await _precompute();
  _initialized = true;
}

/**
 * Walk the content tree once (Brief 018) to compute the list-level gate:
 *   • companion audio of each free AudioHTML item → also unlocked, and
 *   • the set of list IDs with ≥1 free descendant item (transitive).
 *
 * Soft-fails like init(): on any error the precomputed sets are left as-is
 * (free items still play; lists fall back to locked), never throwing.
 *
 * @returns {Promise<void>}
 */
async function _precompute() {
  if (_precomputed) return;
  try {
    await content.loadAll();

    // Freeing an AudioHTML item must also free its companion Audio so the
    // memory-hook audio plays (Brief 018 gotcha). Resolve companions now.
    for (const id of FREE_ITEMS) {
      const item = await content.getItem(id);
      if (item?.type === "AudioHTML" && item.companionAudioId) {
        _unlockedItems.add(item.companionAudioId);
      }
    }

    await _walkList("root", new Set());
    _precomputed = true;
  } catch (err) {
    console.warn("[entitlement] list precompute failed", err);
  }
}

/**
 * DFS one list: returns whether its subtree contains ≥1 free item, and records
 * every list with a free descendant into `_unlockedLists`. A list child is
 * always recursed (so nested lists are recorded even once a free item is
 * found); a free item child counts toward the unlock.
 *
 * @param {string} listId
 * @param {Set<string>} visiting  cycle guard for malformed trees
 * @returns {Promise<boolean>}
 */
async function _walkList(listId, visiting) {
  if (visiting.has(listId)) return _unlockedLists.has(listId);
  visiting.add(listId);
  const node = await content.getList(listId);
  if (!node) return false;
  let hasFree = false;
  for (const child of node.items) {
    if (content.isListNode(child.id)) {
      if (await _walkList(child.id, visiting)) hasFree = true;
    } else if (_unlockedItems.has(child.id)) {
      hasFree = true;
    }
  }
  if (hasFree) _unlockedLists.add(listId);
  return hasFree;
}

/**
 * Is this item unlocked for the current user?
 *
 * Entitled → everything. Otherwise → only the FREE_ITEMS allowlist. While
 * init() is still pending, `_entitled` is false and this returns the free set
 * — the deliberate "default-open-to-free while pending" behaviour so the UI
 * never flashes a lockout on boot.
 *
 * @param {string} itemId
 * @returns {boolean}
 */
export function isUnlocked(itemId) {
  if (_entitled) return true;
  return _unlockedItems.has(itemId);
}

/**
 * Is this list unlocked for the current user? Entitled → every list.
 * Otherwise → only lists with ≥1 free descendant item (precomputed at init();
 * O(1) set lookup). A list missing from the set (e.g. precompute hasn't run or
 * soft-failed) reads as locked — the deliberate conservative default.
 *
 * @param {string} listId
 * @returns {boolean}
 */
export function isListUnlocked(listId) {
  if (_entitled) return true;
  return _unlockedLists.has(listId);
}

/** @returns {boolean} True once init() has resolved. */
export function isInitialized() {
  return _initialized;
}

/** @returns {EntitlementStatus} The resolved (or pending-default) status. */
export function status() {
  return _status;
}
